import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { demoAPI } from '../lib/api';
import { isAuthenticated } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import useDocumentTitle from '../lib/useDocumentTitle';
import RCLogo from '../components/Logo';
import { Button } from '../components/ui/button';
import {
  Loader2, AlertCircle, ArrowRight, Sparkles, CheckCircle2,
  RefreshCw, Share2, Wand2, Save,
} from 'lucide-react';

/**
 * `/demo/:token`. Public share page for the viral "paste Willhaben
 * URL → see a reel" funnel. No auth needed; the token in the URL is
 * the access.
 *
 * Three operating states (driven by the backend status field):
 *   - **rendering**. Show the listing headline + a polished loading
 *     animation. Poll the backend every 4s for status.
 *   - **succeeded**. Autoplay the rendered 9:16 video, surround it
 *     with three CTAs (Save = signup, Customise = signup, More
 *     templates = signup), all routing to `/auth?demo=<token>` so
 *     AuthPage can claim the render right after signup.
 *   - **failed**. Friendly error + "Try another listing" link back
 *     to the landing page paste-bar.
 *
 * Mobile-first: 9:16 video sits centred; CTAs stack below on small
 * screens, side-by-side on md+.
 */
export default function DemoSharePage() {
  const { token } = useParams();
  const { t } = useTranslation();
  useDocumentTitle('Dein Demo-Reel · Real Content');

  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  // Used to suppress the loading skeleton on subsequent polls (only
  // the very first fetch shows the full-page spinner; after that
  // we show the listing card + a smaller render-progress indicator).
  const [initialLoad, setInitialLoad] = useState(true);
  const pollTimerRef = useRef(null);

  const loggedIn = useRef(isAuthenticated()).current;

  // The terminal statuses where polling stops. Anything else (including
  // unknown/legacy values) keeps polling. We'd rather over-poll than
  // get stuck on a transient string the backend may rename later.
  const TERMINAL = new Set(['succeeded', 'completed', 'failed', 'error']);

  // Single fetch. Used both for the initial load and for polling.
  // Re-uses one timer ref so a slow tab or unmount doesn't stack
  // pollers.
  useEffect(() => {
    let cancelled = false;

    const fetchOnce = async () => {
      try {
        const { data: payload } = await demoAPI.getStatus(token);
        if (cancelled) return;
        setData(payload);
        setInitialLoad(false);
        if (!TERMINAL.has(payload.status)) {
          pollTimerRef.current = setTimeout(fetchOnce, 4000);
        }
      } catch (err) {
        if (cancelled) return;
        const msg = err?.response?.data?.detail
          || t('demo.errorLoad')
          || 'Diese Demo konnte nicht geladen werden.';
        setError(msg);
        setInitialLoad(false);
      }
    };

    fetchOnce();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
    // Token is stable for the page's lifetime. Re-run only if it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // CTA destination. Logged-in users skip auth and go straight to the
  // claim endpoint via the dashboard URL. But the claim API requires
  // auth, so we route them through /auth?demo=... which will detect
  // an existing session and claim immediately. Simpler than building
  // a separate authenticated-claim path.
  const signupUrl = `/auth?demo=${encodeURIComponent(token)}`;

  // ── Loading skeleton (only on very first fetch) ───────────────
  if (initialLoad) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-4">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-[#1f4d3a] animate-spin mx-auto mb-4" />
          <p className="text-[#6f6a5c]">{t('demo.loading') || 'Lade Demo…'}</p>
        </div>
      </div>
    );
  }

  // ── Hard error (token missing / network down) ────────────────
  if (error) {
    return (
      <div className="min-h-screen bg-[#f7f3ea] flex items-center justify-center px-4">
        <div className="glass p-8 rounded-2xl max-w-md w-full text-center">
          <AlertCircle className="w-10 h-10 text-[#b3543f] mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-[#23211a] mb-2">
            {t('demo.errorTitle') || 'Hier stimmt was nicht'}
          </h1>
          <p className="text-[#6f6a5c] mb-6">{error}</p>
          <Link to="/">
            <Button className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white">
              {t('demo.backToLanding') || 'Zur Startseite'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const status = data?.status;
  const isRendering = !TERMINAL.has(status);
  const isFailed = status === 'failed' || status === 'error';
  const isReady = (status === 'succeeded' || status === 'completed') && data?.video_url;

  return (
    <div className="min-h-screen bg-[#f7f3ea]">
      {/* Subtle animated background. Same palette as the landing
          page so the visual continuity is obvious. */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1f4d3a]/10 via-transparent to-transparent" />
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#1f4d3a]/30 rounded-full blur-3xl" />
        <div className="absolute top-1/2 -left-40 w-96 h-96 bg-[#f0e6cf] rounded-full blur-3xl" />
      </div>

      {/* Slim header. Just the logo + a back-to-landing affordance.
          We don't want a full nav on the share page; the page is a
          conversion focused funnel, not a hub. */}
      <header className="relative z-10 max-w-6xl mx-auto px-4 py-5 flex items-center justify-between">
        <Link to="/" className="inline-block">
          <RCLogo size="md" />
        </Link>
        {!loggedIn && (
          <Link to="/auth" className="text-sm text-[#6f6a5c] hover:text-[#23211a] transition-colors">
            {t('landing.signIn') || 'Anmelden'}
          </Link>
        )}
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-4 pt-6 pb-20">
        {/* Headline. Listing title (if scraped) or generic fallback */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#e9efe7] border border-[#1f4d3a]/30 rounded-full mb-4">
            <Sparkles className="w-3.5 h-3.5 text-[#1f4d3a]" />
            <span className="text-xs uppercase tracking-wider text-[#1f4d3a]">
              {t('demo.previewBadge') || 'Dein Demo-Reel'}
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#23211a] mb-2 leading-tight">
            {data?.title || t('demo.fallbackTitle') || 'Deine Immobilie als Reel'}
          </h1>
          {(data?.location || data?.price) && (
            <p className="text-[#6f6a5c] text-sm md:text-base">
              {[data?.location, data?.price].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        {/* Video / progress card */}
        <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(280px,400px)] gap-6 lg:gap-8 items-start">
          {/* Video column. 9:16, autoplay muted. Tap to unmute. Re-uses
              the same handler shape as the landing-page examples
              (bug-fixed previous-tap-state snapshot). */}
          <div className="relative mx-auto w-full max-w-[420px] md:max-w-none">
            <div className="relative overflow-hidden rounded-2xl aspect-[9/16] bg-black border border-[#e3dccb] shadow-2xl">
              {isReady ? (
                <video
                  src={data.video_url}
                  poster={data.thumbnail_url || undefined}
                  className="w-full h-full object-cover cursor-pointer"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  onClick={(e) => {
                    const target = e.currentTarget;
                    target.muted = !target.muted;
                  }}
                  data-testid="demo-video"
                />
              ) : (
                // Render-in-progress. Skeleton with the listing's
                // first image (if we have a thumbnail) and a friendly
                // wait message. Eyes have something to look at, so
                // ~60s of render feels shorter.
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-gradient-to-br from-[#dfe8db] via-[#fffdf7] to-[#f0e6cf]">
                  {data?.thumbnail_url && (
                    <img
                      src={data.thumbnail_url}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 w-full h-full object-cover opacity-30 blur-md"
                    />
                  )}
                  <div className="relative">
                    {isFailed ? (
                      <>
                        <AlertCircle className="w-10 h-10 text-[#b3543f] mx-auto mb-3" />
                        <p className="text-[#23211a] font-medium mb-1">
                          {t('demo.failedTitle') || 'Render fehlgeschlagen'}
                        </p>
                        <p className="text-[#6f6a5c] text-sm">
                          {t('demo.failedBody') || 'Versuch es nochmal mit einer anderen Anzeige.'}
                        </p>
                      </>
                    ) : (
                      <>
                        <Loader2 className="w-10 h-10 text-[#1f4d3a] animate-spin mx-auto mb-4" />
                        <p className="text-[#23211a] font-medium mb-1">
                          {t('demo.renderingTitle') || 'Wir rendern dein Reel…'}
                        </p>
                        <p className="text-[#6f6a5c] text-sm">
                          {t('demo.renderingHint') || 'Voiceover, Animationen, Branding. Ca. 60 Sekunden.'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            {isReady && (
              <p className="text-center text-xs text-[#8a8474] mt-3">
                {t('demo.tapHint') || 'Tippe das Video an, um den Voiceover zu hören'}
              </p>
            )}
          </div>

          {/* Side column. The conversion stack. Primary CTA is "save
              to my account" (signup-with-claim); below that two
              softer CTAs frame what they UNLOCK by signing up. */}
          <aside className="space-y-3">
            {isReady ? (
              <>
                <div className="glass border border-[#1f4d3a]/40 rounded-2xl p-5">
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 rounded-lg bg-[#e9efe7] shrink-0">
                      <Save className="w-5 h-5 text-[#1f4d3a]" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-[#23211a] leading-tight">
                        {t('demo.saveTitle') || 'Reel in deinen Account speichern'}
                      </h2>
                      <p className="text-sm text-[#6f6a5c] mt-1">
                        {t('demo.saveBody')
                          || 'Erstelle in 30 Sekunden ein kostenloses Konto. Und das Video wird in deinem Dashboard sichtbar.'}
                      </p>
                    </div>
                  </div>
                  <Link to={signupUrl} className="block">
                    <Button
                      data-testid="demo-claim-cta"
                      className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary py-5 text-base"
                    >
                      {loggedIn
                        ? (t('demo.claimNow') || 'Jetzt in mein Konto speichern')
                        : (t('demo.signupCta') || 'Kostenloses Konto erstellen')}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <p className="text-xs text-[#8a8474] mt-2.5 text-center">
                    {t('demo.signupHint') || 'Keine Kreditkarte · 5 Renders gratis pro Monat'}
                  </p>
                </div>

                {/* Two soft upsells. What they unlock with an account. */}
                <div className="glass border border-[#e3dccb] rounded-xl p-4 flex items-start gap-3">
                  <Wand2 className="w-4 h-4 text-[#1f4d3a] mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#23211a]">
                      {t('demo.unlockTemplates') || 'Andere Vorlagen probieren'}
                    </p>
                    <p className="text-xs text-[#8a8474] mt-0.5">
                      {t('demo.unlockTemplatesBody') || '11 Templates. Story, Reel, Feed-Post.'}
                    </p>
                  </div>
                </div>
                <div className="glass border border-[#e3dccb] rounded-xl p-4 flex items-start gap-3">
                  <Share2 className="w-4 h-4 text-[#8f6f3a] mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-[#23211a]">
                      {t('demo.unlockAutoPost') || 'Auto-Post auf 6 Plattformen'}
                    </p>
                    <p className="text-xs text-[#8a8474] mt-0.5">
                      {t('demo.unlockAutoPostBody') || 'Instagram, TikTok, Facebook, YouTube, LinkedIn, X.'}
                    </p>
                  </div>
                </div>
              </>
            ) : isFailed ? (
              <div className="glass border border-[#b3543f]/40 rounded-2xl p-5 text-center">
                <p className="text-[#4a463b] mb-4 text-sm">
                  {t('demo.failedRetryHint') || 'Manchmal antwortet Willhaben langsam. Probier es mit einer anderen Anzeige.'}
                </p>
                <Link to="/">
                  <Button className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white w-full">
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('demo.tryAnother') || 'Andere Immobilie probieren'}
                  </Button>
                </Link>
              </div>
            ) : (
              // Still rendering. Short reassurance + locked CTA so
              // the visual hierarchy stays consistent across states.
              <div className="glass border border-[#e3dccb] rounded-2xl p-5">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle2 className="w-5 h-5 text-[#1e7a4f] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#4a463b]">
                    {t('demo.queuedHint')
                      || 'Anzeige eingelesen. Render läuft. Sobald das Video fertig ist, kannst du es kostenlos in dein Konto speichern.'}
                  </p>
                </div>
                <Button
                  disabled
                  className="w-full bg-[#e9e2d2] text-[#8a8474] cursor-not-allowed py-5"
                >
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('demo.renderingButton') || 'Render läuft…'}
                </Button>
              </div>
            )}
          </aside>
        </div>

        {/* Bottom strip. Small "what is this?" pitch for share-page
            visitors who arrived from a tag/share (didn't paste the
            URL themselves). */}
        <div className="mt-16 text-center max-w-2xl mx-auto">
          <p className="text-sm text-[#8a8474] leading-relaxed">
            {t('demo.footerPitch')
              || 'Dieses Video wurde automatisch aus einem Willhaben-Inserat erstellt. Von Real Content. Verbinde Justimmo oder Willhaben und wir machen täglich eines für dich.'}{' '}
            <Link to="/" className="text-[#1f4d3a] hover:text-[#1f4d3a] underline">
              {t('demo.footerLearnMore') || 'Mehr erfahren'}
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
